import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api/client';
import { GlobalNavigation } from '@/components/global-navigation';
import { LandingFooter } from '@/components/landing/landing-footer';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedLoader } from '@/components/ui/animated-loader';
import { ArrowLeft, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface Article {
  id: string;
  slug: string;
  title: string;
  meta_description: string;
  content: string;
  target_keyword: string;
  published_at: string;
}

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['blog-article', slug],
    queryFn: () => apiClient.get(`/blog/articles/${slug}`) as unknown as Promise<Article>,
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <AnimatedLoader message="Loading article..." />
        </div>
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-slate-900 mb-2">Article not found</h1>
            <Link to="/blog" className="text-blue-600 hover:underline">Back to all articles</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.meta_description}
        canonicalUrl={`https://aiexpertnetwork.com/blog/${slug}`}
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.meta_description,
          url: `https://aiexpertnetwork.com/blog/${slug}`,
          publisher: {
            '@type': 'Organization',
            name: 'AI Expert Network',
            url: 'https://aiexpertnetwork.com',
          },
          datePublished: article.published_at,
        }}
      />
      <GlobalNavigation />
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> All articles
          </Link>

          <article>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
              <Calendar className="h-4 w-4" />
              <span>{new Date(article.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>

            <div className="prose prose-slate prose-lg max-w-none prose-headings:text-slate-900 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>
          </article>

          <div className="mt-12 bg-blue-50 border border-blue-200 rounded-2xl p-8 text-center">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Need AI expertise?</h2>
            <p className="text-slate-600 mb-4">
              Browse our network of vetted AI consultants and developers.
            </p>
            <Link
              to="/sign-up"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Find an Expert
            </Link>
          </div>
        </div>
      </div>
      <LandingFooter />
    </>
  );
}
